import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import UploadButton from "@/components/UploadButton";
import { Image as ImageIcon, Plus, X, Trash2, Heart, MessageSquare, Send, ChevronLeft, FolderPlus, Pencil } from "lucide-react";
import { toast } from "sonner";

export const PhotoGallery = ({ proId, isOwner }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [current, setCurrent] = useState(null); // {id, name} or null = albums overview
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null); // lightbox photo
  const [comment, setComment] = useState("");
  const [showNewAlbum, setShowNewAlbum] = useState(false);
  const [albumName, setAlbumName] = useState("");

  const loadAlbums = useCallback(() => {
    api.get(`/photos/albums/${proId}`).then(r => setAlbums(r.data)).catch(() => {});
  }, [proId]);
  const loadPhotos = useCallback((albumId) => {
    api.get(`/photos/by-pro/${proId}`, { params: albumId ? { album_id: albumId } : {} })
      .then(r => setPhotos(r.data)).catch(() => {});
  }, [proId]);

  useEffect(() => { loadAlbums(); }, [loadAlbums]);

  const openAlbum = (a) => { setCurrent(a); loadPhotos(a.id); };
  const backToAlbums = () => { setCurrent(null); setPhotos([]); loadAlbums(); };

  const createAlbum = async () => {
    const name = albumName.trim();
    if (!name) return;
    try {
      await api.post("/photos/albums", { name });
      setAlbumName(""); setShowNewAlbum(false);
      loadAlbums();
      toast.success("Album created");
    } catch { toast.error("Could not create album"); }
  };
  const renameAlbum = async (a) => {
    const name = window.prompt("Rename album", a.name);
    if (!name || !name.trim()) return;
    try { await api.patch(`/photos/albums/${a.id}`, { name: name.trim() }); loadAlbums(); toast.success("Renamed"); }
    catch { toast.error("Could not rename"); }
  };
  const deleteAlbum = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? Its photos move to General.`)) return;
    try { await api.delete(`/photos/albums/${a.id}`); loadAlbums(); toast.success("Album deleted"); }
    catch { toast.error("Could not delete album"); }
  };
  const uploadPhoto = async (url) => {
    try {
      await api.post("/photos", { image_url: url, album_id: current?.id || null });
      current ? loadPhotos(current.id) : loadAlbums();
      loadAlbums();
      toast.success("Photo added");
    } catch { toast.error("Could not add photo"); }
  };
  const deletePhoto = async (p) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/photos/${p.id}`);
      setActive(null);
      current ? loadPhotos(current.id) : loadAlbums();
      loadAlbums();
      toast.success("Photo deleted");
    } catch { toast.error("Could not delete photo"); }
  };

  const requireLogin = () => {
    if (!user) { toast("Please sign in first"); navigate("/login"); return false; }
    return true;
  };
  const refreshActive = async (id) => {
    const r = await api.get(`/photos/${id}`);
    setActive(r.data);
    if (current) loadPhotos(current.id);
  };
  const toggleReact = async (p) => {
    if (!requireLogin()) return;
    try { await api.post(`/photos/${p.id}/react`); refreshActive(p.id); }
    catch { toast.error("Could not react"); }
  };
  const submitComment = async (p) => {
    if (!requireLogin()) return;
    if (!comment.trim()) return;
    try {
      await api.post(`/photos/${p.id}/comment`, { text: comment });
      setComment("");
      refreshActive(p.id);
    } catch { toast.error("Could not comment"); }
  };
  const removeComment = async (p, c) => {
    try { await api.delete(`/photos/${p.id}/comment/${c.id}`); refreshActive(p.id); }
    catch { toast.error("Could not remove"); }
  };

  const hasReacted = active && user && (active.reactions || []).some(r => r.user_id === user.id);

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          {current ? (
            <button onClick={backToAlbums} className="flex items-center gap-2 hover:text-[#245544]" data-testid="albums-back">
              <ChevronLeft size={20}/> {current.name}
            </button>
          ) : (<><ImageIcon size={20}/> Photos</>)}
        </h2>
        {isOwner && (
          <div className="flex gap-2">
            {!current && (
              <button onClick={() => setShowNewAlbum(true)} className="px-4 py-2 rounded-full bg-white border border-black/10 text-sm flex items-center gap-1.5 hover:bg-black/5" data-testid="new-album-btn">
                <FolderPlus size={15}/> New album
              </button>
            )}
            <UploadButton onUploaded={uploadPhoto} label={current ? `Add to ${current.name}` : "Add photo"} className="!bg-[#2E6B57] !border-0 !text-[#FFFFFF]" />
          </div>
        )}
      </div>

      {isOwner && showNewAlbum && !current && (
        <div className="bg-white rounded-xl p-4 border border-black/10 mb-4 flex gap-2" data-testid="new-album-form">
          <input autoFocus value={albumName} onChange={e => setAlbumName(e.target.value)} maxLength={60}
            onKeyDown={e => e.key === "Enter" && createAlbum()}
            placeholder="Album name, e.g. Kitchen Remodels"
            className="flex-1 px-3 py-2 border border-black/10 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="new-album-input"/>
          <button onClick={createAlbum} className="btn-lime px-4 py-2 text-sm" data-testid="new-album-save">Create</button>
          <button onClick={() => { setShowNewAlbum(false); setAlbumName(""); }} className="px-3 py-2 text-sm">Cancel</button>
        </div>
      )}

      {/* Albums overview */}
      {!current && (
        albums.every(a => a.count === 0) && !isOwner ? (
          <p className="text-[#222]/50 text-sm">No photos yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="albums-grid">
            {albums.filter(a => a.count > 0 || (isOwner && !a.system) || (a.system && (isOwner || a.count > 0))).map(a => (
              <div key={a.id} className="relative group">
                <button onClick={() => openAlbum(a)} className="w-full text-left" data-testid={`album-${a.id}`}>
                  <div className="aspect-square rounded-xl overflow-hidden bg-black/5">
                    {a.cover
                      ? <img src={a.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                      : <div className="w-full h-full grid place-items-center text-[#222]/30"><ImageIcon size={28}/></div>}
                  </div>
                  <p className="font-semibold text-sm mt-1.5 truncate">{a.name}</p>
                  <p className="text-xs text-[#222]/50">{a.count} photo{a.count === 1 ? "" : "s"}</p>
                </button>
                {isOwner && !a.system && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => renameAlbum(a)} className="p-1.5 bg-white/90 rounded-full shadow hover:text-[#245544]" data-testid={`album-rename-${a.id}`}><Pencil size={13}/></button>
                    <button onClick={() => deleteAlbum(a)} className="p-1.5 bg-white/90 rounded-full shadow hover:text-red-500" data-testid={`album-delete-${a.id}`}><Trash2 size={13}/></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* Inside an album — photo grid */}
      {current && (
        photos.length === 0 ? (
          <p className="text-[#222]/50 text-sm">No photos in this album yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photos-grid">
            {photos.map(p => (
              <button key={p.id} onClick={() => setActive(p)} className="aspect-square rounded-xl overflow-hidden bg-black/5 group relative" data-testid={`photo-${p.id}`}>
                <img src={p.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-2 flex items-center gap-3 text-white text-xs opacity-0 group-hover:opacity-100 transition">
                  <span className="flex items-center gap-1"><Heart size={12}/> {(p.reactions || []).length}</span>
                  <span className="flex items-center gap-1"><MessageSquare size={12}/> {(p.comments || []).length}</span>
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {/* Lightbox */}
      {active && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={() => setActive(null)} data-testid="photo-lightbox">
          <div className="bg-white rounded-2xl overflow-hidden w-full max-w-4xl max-h-[90vh] flex flex-col md:flex-row" onClick={e => e.stopPropagation()}>
            <div className="md:w-3/5 bg-black grid place-items-center">
              <img src={active.image_url} alt="" className="w-full max-h-[45vh] md:max-h-[90vh] object-contain"/>
            </div>
            <div className="md:w-2/5 flex flex-col max-h-[45vh] md:max-h-[90vh]">
              <div className="p-4 border-b border-black/5 flex items-start justify-between gap-2">
                <p className="text-sm text-[#222]/80">{active.caption || "Photo"}</p>
                <div className="flex items-center gap-1 shrink-0">
                  {isOwner && <button onClick={() => deletePhoto(active)} className="p-1.5 text-[#222]/40 hover:text-red-500" data-testid="lightbox-delete-photo"><Trash2 size={16}/></button>}
                  <button onClick={() => setActive(null)} className="p-1.5 text-[#222]/40 hover:text-black" data-testid="lightbox-close"><X size={18}/></button>
                </div>
              </div>
              <div className="px-4 py-2 border-b border-black/5">
                <button onClick={() => toggleReact(active)} className={`flex items-center gap-1.5 text-sm font-semibold ${hasReacted ? "text-red-500" : "text-[#222]/60 hover:text-red-500"}`} data-testid="lightbox-react">
                  <Heart size={17} className={hasReacted ? "fill-red-500" : ""}/> {(active.reactions || []).length} {(active.reactions || []).length === 1 ? "like" : "likes"}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="lightbox-comments">
                {(active.comments || []).length === 0 && <p className="text-sm text-[#222]/40">No comments yet. Say something nice!</p>}
                {(active.comments || []).map(c => (
                  <div key={c.id} className="flex gap-2 group" data-testid={`photo-comment-${c.id}`}>
                    <div className="w-7 h-7 rounded-full bg-[#F2F4F2] grid place-items-center text-xs font-bold shrink-0">{c.user_name?.[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm"><span className="font-semibold">{c.user_name}</span> {c.text}</p>
                      <p className="text-[11px] text-[#222]/40">{new Date(c.created_at).toLocaleDateString()}</p>
                    </div>
                    {user && (user.id === c.user_id || isOwner) && (
                      <button onClick={() => removeComment(active, c)} className="opacity-0 group-hover:opacity-100 text-[#222]/30 hover:text-red-500" data-testid={`photo-comment-delete-${c.id}`}><X size={13}/></button>
                    )}
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-black/5 flex gap-2">
                <input value={comment} onChange={e => setComment(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && submitComment(active)}
                  placeholder={user ? "Add a comment…" : "Sign in to comment"}
                  className="flex-1 px-3 py-2 rounded-full bg-[#F2F4F2] border border-black/10 text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="photo-comment-input"/>
                <button onClick={() => submitComment(active)} className="btn-lime px-4 py-2 text-sm" data-testid="photo-comment-submit"><Send size={14}/></button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
